import { defineEffect } from '../../../core/effect';
import { noise1, seeded, seg, tween } from '../../../core/effect/motion';
import {
  add, canvasOf, fade, fill, place, playerColor, sprite,
} from '../fx/common';
import {
  bankFrame, formulaRow, materialCards, monsterPlate,
} from '../fx/materialBank';
import phaseFlame from '../fx/phaseFlame.png';

/**
 * **アドバンス召喚の儀式**（Issue #648 v1。指示書 `../direction/material_summon_tribute.md`。キービジュアルは無い＝方向は人が決めた）。
 * 疑似イベント `material_summon`（`editorPorts/materialSummon.js`）の状況 `material_summon_tribute`。**motion なし**（盤は触らない。
 * リリースしたカードはこの前の move でもう墓地、出るモンスターは後の `summoning` / `set` で盤に出る＝召喚の宣言のバンクと 2 回あってよい）。
 * 骨組み（並び・数字の叩き込み・モンスターの板・はけ）はシンクロ（`material_summon_synchro_effect.js`）と共有（`../fx/materialBank.js`）。
 *
 * ```
 * 0      溜め: 舞台を赤黒く暗く。リリースしたカード（表）が召喚した人の側から画面の中央へ来て並ぶ（1 体なら中央、2 体なら左右）
 * 340    「リリース」→ 520「×2」（数字だけ大きく赤金）を叩きつける。叩くたびにカードが揺れて硬い縁が光る。
 *        360〜560 足元に暗い魔法陣（赤の硬い線の輪・角の楔・五芒星。床に寝かせた楕円）が開いて回る
 * 800    **炎に包まれる**: 魔法陣から暗い炎（深い赤 → 赤 → 黒の段の板。12 コマで描き替える）が立ち上がりカードを包む。
 *        カードは下から黒く焦げ（焦げの縁は赤い硬いぎざぎざの線）、1100〜1280 で下から崩れて灰（黒い小片）になる。全部のカードが同時に
 * 1250   灰が舞い上がって画面の中央へ渦を巻いて集まる → 1400 閃光（赤）1 フレーム・ヒットストップ 100ms・揺れ。魔法陣が割れて広がり消える
 * 1500   灰の中から出るモンスター: 鋼の板とイラストが縦の細い隙間から開き、影 → 色。1700 名前、1780 ADVANCE SUMMON の帯（縁はプレイヤーの色と赤）。
 *        セット（`result.faceDown`）なら絵も名前も見せない（その人のスリーブの裏のカード。帯は ADVANCE SET）。
 *        出るモンスターがまだ入力されていなければ灰が散って終わる（尺も 2200 に縮む）
 * 2600   全部が縦に細く潰れて消え、暗幕を戻す。何も残さない
 * ```
 *
 * 炎・灰・出るモンスターは**画面の中央に固定**（シンクロ v3 で決まったこと）。炎の素材は new_phase のバトルの炎と同じ絵（`../fx/phaseFlame.png`）を
 * 暗い色に染めて使う（もやもやにしない＝硬い形の板）。見出しは「リリース ×n」（ユーザー 2026-10-01）。
 */
export const D = 2800;
export const D_NO_RESULT = 2200;
const WORD_HIT = 340;
const NUM_HIT = 520;
const SIGIL_OPEN = [360, 560];
const FLAME = [800, 950];       // 炎が立ち上がる
const FLAME_DIE = [1250, 1380];
const CHAR = [850, 1100];       // 下から焦げる
const CRUMBLE = [1100, 1280];   // 下から崩れて灰に
const GATHER = [1250, 1400];
const HIT = 1400;
const STOP = 100;
const MON = 1500;
const MON_OPEN = 120;
const MON_COLOR = [1560, 1700];
const NAME_HIT = 1700;
const BAND_HIT = 1780;

const RITUAL_RED = '#c0141a';
const NUM_COLOR = '#ff7a30';
const FLAME_ASPECT = 1400 / 483;
const ASH_PER_CARD = 44;
const EMBERS = 30;

/** 帯の尺（出るモンスターが無ければ灰が散って終わる） */
export const durationOf = (payload) => (payload?.result?.code != null ? D : D_NO_RESULT);

/** 見出し（純関数）: 「リリース」と「×n」（n はリリースした数。1 未満なら 1） */
export function tributeHeading(payload) {
  const n = Math.max(1, Array.isArray(payload?.materials) ? payload.materials.length : 0);
  return [{ text: 'リリース', size: 110, at: WORD_HIT }, { text: `×${n}`, size: 170, at: NUM_HIT, color: NUM_COLOR }];
}

/** 帯の文字（セットなら ADVANCE SET） */
export const bandTag = (result) => (result?.faceDown ? 'ADVANCE SET' : 'ADVANCE SUMMON');

/**
 * **リリースしたカードの並び**（純関数）: 画面の中央を中心に等間隔（1 体なら中央、2 体なら左右）。はみ出すなら間を詰める
 */
export function centerRow(n, cw, W = 1920) {
  const cx = W / 2;
  const room = W - 160 - cw;
  const gap = n > 1 ? Math.min(cw + 90, room / (n - 1)) : 0;
  return Array.from({ length: n }, (_, i) => cx + (i - (n - 1) / 2) * gap);
}

function sceneFor(payload) {
  const out = [
    'reset',
    {
      ms: 0,
      grade: {
        contrast: {
          keys: [
            { ms: HIT - 1, value: 1 }, { ms: HIT, value: 1.45 }, { ms: HIT + STOP, value: 1.45, ease: 'hold' }, { ms: HIT + STOP + 1, value: 1 },
          ],
        },
        chroma: {
          keys: [
            { ms: NUM_HIT - 1, value: 0 }, { ms: NUM_HIT, value: 0.01 }, { ms: NUM_HIT + 34, value: 0, ease: 'ease_out' },
            { ms: HIT - 1, value: 0 }, { ms: HIT, value: 0.016 }, { ms: HIT + 34, value: 0, ease: 'ease_out' },
          ],
        },
      },
    },
    { ms: WORD_HIT + 10, shake: { amp: 12, freq: 26, decayMs: 200 } },
    { ms: NUM_HIT + 10, shake: { amp: 22, freq: 24, decayMs: 280 } },
    { ms: FLAME[0], shake: { amp: 8, freq: 18, decayMs: 400 } },
    { ms: HIT + STOP, shake: { amp: 34, freq: 22, decayMs: 420 } },
  ];
  if (payload?.result?.code != null) {
    if (!payload.result.faceDown) out.push({ ms: NAME_HIT, shake: { amp: 8, freq: 30, decayMs: 160 } });
    out.push({ ms: BAND_HIT, shake: { amp: 14, freq: 26, decayMs: 220 } });
  }
  return out;
}

export default defineEffect({
  situation: 'material_summon_tribute',
  role: 'effect',
  label: 'アドバンス召喚の儀式（リリースの数 → 暗い炎で灰に → 灰の中から出るモンスター）',
  color: RITUAL_RED,
  duration: ({ payload } = {}) => durationOf(payload),
  scene: (ctx) => sceneFor(ctx?.payload),
  sounds: ({ payload } = {}) => {
    const out = [
      { at: 0, name: 'phase_change', gain: 0.45 },
      { at: WORD_HIT - 10, name: 'attack', gain: 0.7 },
      { at: NUM_HIT - 10, name: 'attack', gain: 0.95 },
      { at: FLAME[0], name: 'battle_phase', gain: 0.6 },
      { at: CRUMBLE[0], name: 'destoroy', gain: 0.5 },
      { at: HIT - 15, name: 'attack', gain: 0.9 },
    ];
    if (payload?.result?.code != null) out.push({ at: MON, name: 'summon' });
    return out;
  },

  layers: [{
    id: 'bank',
    label: 'リリースの数 → 暗い炎で灰に → 灰の中から出るモンスター',
    space: 'screen',
    script: (ctx) => {
      const { THREE, kit } = ctx;
      const { w: W, h: H } = canvasOf(ctx);
      const { payload } = ctx;
      const p = payload.player === 1 ? 1 : 0;
      const pc = playerColor(ctx, p);
      const materials = Array.isArray(payload.materials) ? payload.materials : [];
      const result = payload.result?.code != null ? payload.result : null;
      const dur = durationOf(payload);
      const rand = seeded(`${ctx.seed}:tribute`);
      const frame = bankFrame(ctx, { dur, dim: '#0b0204', dimAlpha: 0.72 });
      const { layer } = frame;
      const CX = W / 2;
      const GY = 540;              // 灰が集まる所（出るモンスターの板の中ほど）

      // --- リリースしたカード（画面の中央に並ぶ）
      const n = materials.length;
      const CW = Math.min(260, 1500 / Math.max(1, n) - 90);
      const ROW_Y = 580;
      const heading = tributeHeading(payload);
      const row = materialCards(ctx, frame, {
        materials, player: p, xs: centerRow(n, CW, W), y: ROW_Y, cw: CW, rand, hitsOf: () => [WORD_HIT, NUM_HIT],
      });
      const CH = row.ch;
      const FOOT = ROW_Y + CH / 2 + 14;    // 足元（魔法陣・炎の根元）
      const rowW = n > 0 ? Math.abs(row.items[n - 1].x - row.items[0].x) + CW : CW;

      // --- 足元の魔法陣（床に寝かせた楕円。group で潰し、中の板を回す）
      const SIGIL_W = Math.max(720, rowW + 300);
      const sigilG = add(layer(2), new THREE.Group(), 2);
      const sigil = add(sigilG, sprite(ctx, kit.hardSigilTexture(RITUAL_RED, { size: 1024, points: 8 }), 1, 1), 2);

      // --- カードごとの焦げ・炎
      const flameTex = kit.urlTexture(phaseFlame);
      const FLAME_TINTS = [
        { tint: '#4a0508', w: 2.3, h: 1.75, every: 3, phase: 0, delay: 0, order: 3 },
        { tint: '#a3140f', w: 1.9, h: 1.45, every: 2, phase: 1, delay: 40, order: 3.1 },
        { tint: '#0a0204', w: 1.6, h: 1.15, every: 4, phase: 2, delay: 90, order: 5.5 },
      ];
      const burns = row.items.map((mc, ci) => {
        const darken = add(layer(4.2), fill(ctx, '#2a0604', CW, CH), 4.2);
        const char = add(layer(4.3), fill(ctx, '#0a0204', CW, CH), 4.3);
        const edge = Array.from({ length: 7 }, () => ({ m: add(layer(4.4), fill(ctx, '#ff4a1a', 1, 1), 4.4), dy: (rand() - 0.5) * 22, rot: (rand() - 0.5) * 0.5 }));
        const flames = FLAME_TINTS.map((f, k) => {
          const m = add(layer(f.order), sprite(ctx, flameTex, CW * f.w, CW * f.w / FLAME_ASPECT), f.order);
          m.material.color?.set?.(f.tint);
          return { ...f, m, key: `${ci}:${k}` };
        });
        return { mc, darken, char, edge, flames };
      });

      // --- 灰（崩れる線から生まれ、舞い上がって中央へ渦を巻いて集まり、閃光のあと散る）
      const ashCols = ['#4a4244', '#1a1416', '#6b5d5c', '#2a2224', '#857472', '#ff5a1a'];
      const ash = [];
      row.items.forEach((mc) => {
        for (let k = 0; k < ASH_PER_CARD; k += 1) {
          const ember = k % 6 === 5;
          const m = new THREE.Mesh(new THREE.CircleGeometry(1, k % 2 === 0 ? 3 : 4), kit.plain(null, 1));
          m.material.color?.set?.(ashCols[k % ashCols.length]);
          add(layer(ember ? 6.6 : 6.5), m, ember ? 6.6 : 6.5);
          const u = rand();
          ash.push({
            m,
            born: CRUMBLE[0] + (CRUMBLE[1] - CRUMBLE[0]) * u,
            x: mc.x + (rand() - 0.5) * CW,
            y: mc.y + CH / 2 - u * CH,
            vy: 120 + rand() * 200,
            sway: 20 + rand() * 50,
            size: ember ? 6 + rand() * 6 : 10 + rand() * 14,
            spin: (rand() - 0.5) * 14,
            a: rand() * Math.PI * 2,
            speed: 500 + rand() * 900,
            seed: rand() * 100,
          });
        }
      });

      // 火の粉（硬い菱形。魔法陣から巻き上がり、炎が消える前に燃え尽きる）
      const emberCols = ['#ff5a1a', '#c0141a', '#ff9a3c'];
      const embers = Array.from({ length: EMBERS }, (_, i) => {
        const m = new THREE.Mesh(new THREE.CircleGeometry(1, 4), kit.plain(null, 1));
        m.material.color?.set?.(emberCols[i % emberCols.length]);
        add(layer(5.6), m, 5.6);
        return {
          m, born: FLAME[0] + rand() * 380, life: 260 + rand() * 200, x: CX + (rand() - 0.5) * (rowW + 120), vy: 420 + rand() * 420, size: 6 + rand() * 8, spin: (rand() - 0.5) * 16, i,
        };
      });

      // --- 見出し（リリース ×n）
      const FY = 250;
      const formula = formulaRow(ctx, frame, heading, { gap: 30 });

      // --- 閃光（赤 1 フレーム）
      const flash = add(layer(14), place(fill(ctx, RITUAL_RED, W, H), W / 2, H / 2), 14);

      // --- 出るモンスター（灰の集まった所＝画面の中央）
      const mon = result ? monsterPlate(ctx, frame, {
        result, player: p, pc, accent: '#8a1a14', bandColor: RITUAL_RED, tag: bandTag(result), cx: CX,
      }) : null;

      return {
        root: frame.top,
        update: ({ localMs: t }) => {
          frame.update(t);
          const step = Math.floor(t / 83);   // 12 コマ

          // --- 魔法陣: 開く → 回る → 閃光の後で割れて広がり消える
          const so = tween(t, SIGIL_OPEN[0], SIGIL_OPEN[1], 'expo_out');
          const brk = tween(t, HIT + STOP, HIT + STOP + 140, 'expo_out');
          const sw = SIGIL_W * so * (1 + 0.5 * brk);
          sigilG.position.set(CX, FOOT, 0);
          sigilG.scale.set(Math.max(0.001, sw), Math.max(0.001, sw * 0.3), 1);
          sigil.rotation.z = Math.min(t, HIT) / 1000 * 0.6 + (t >= HIT + STOP ? (t - HIT - STOP) / 1000 * 2 : 0);
          fade(sigil, t >= SIGIL_OPEN[0] && brk < 1 ? 1 - brk : 0);

          // --- カード: 並ぶ → 叩くたびに揺れ・縁 → 炎の中で下から焦げる → 下から崩れる
          const charK = tween(t, CHAR[0], CHAR[1], 'power2_in');
          const crK = seg(t, CRUMBLE[0], CRUMBLE[1]);
          burns.forEach((b) => {
            const { mc } = b;
            const ps = row.pose(mc, t);
            place(mc.c.mesh, ps.x, ps.y, { s: ps.s, rot: ps.rot });
            fade(mc.c.mesh, t < CRUMBLE[0] ? 1 : 0);
            row.edges(mc, ps.x, ps.y, ps.s, ps.lit, ps.rot);
            fade(mc.flash, 0);
            const bottom = mc.y + CH / 2;
            // 全体が暗く煤ける
            place(b.darken, mc.x, mc.y);
            fade(b.darken, t < CRUMBLE[0] ? 0.55 * seg(t, FLAME[0], CHAR[1]) : 0);
            // 下から焦げる → 崩れる（焦げた黒い板が下から削れていく。上の辺は止まったまま）
            const charH = t < CRUMBLE[0] ? CH * charK : CH * (1 - crK);
            const charY = t < CRUMBLE[0] ? bottom - charH / 2 : mc.y - CH / 2 + charH / 2;
            place(b.char, mc.x, charY, { sx: 1, sy: Math.max(0.001, charH / CH) });
            fade(b.char, t >= CHAR[0] && charH > 1 && crK < 1 ? 1 : 0);
            // 焦げ・崩れの縁（赤い硬いぎざぎざの線）
            const lineY = t < CRUMBLE[0] ? bottom - charH : mc.y - CH / 2 + charH;
            const lineOn = t >= CHAR[0] && t < CRUMBLE[1] && charH > 2 && (t >= CRUMBLE[0] || charK < 0.995);
            b.edge.forEach((e, j) => {
              const ex = mc.x - CW / 2 + ((j + 0.5) / b.edge.length) * CW;
              const jig = noise1(`${ctx.seed}:edge${j}`, step * 0.8) * 6;
              place(e.m, ex, lineY + e.dy * 0.4 + jig, { sx: CW / b.edge.length + 10, sy: 6, rot: e.rot });
              fade(e.m, lineOn ? 1 : 0);
            });
            // 暗い炎（段の板。12 コマで描き替え、数コマごとに左右を反転）
            const die = tween(t, FLAME_DIE[0], FLAME_DIE[1], 'power2_in');
            b.flames.forEach((f, k) => {
              const st = Math.floor((t + f.phase * 29) / 83);
              const shape = Math.floor(st / f.every) + f.phase;
              const flip = shape % 2 === 0 ? 1 : -1;
              const nz = noise1(`${ctx.seed}:fl${f.key}`, st * 0.9);
              const up = tween(t, FLAME[0] + f.delay, FLAME[1] + f.delay, 'expo_out');
              const fh0 = (CW * f.w) / FLAME_ASPECT;
              const hgt = CH * f.h * (1 + 0.1 * nz) * up * (1 - die);
              const sy = Math.max(0.001, hgt / fh0);
              place(f.m, mc.x + 10 * noise1(`${ctx.seed}:fx${f.key}`, st * 0.7) + (k - 1) * 8, FOOT - hgt / 2, { sx: flip * (1 - 0.03 * nz), sy });
              fade(f.m, t >= FLAME[0] + f.delay && die < 1 ? 1 : 0);
            });
          });

          // 火の粉
          embers.forEach((e) => {
            const k = (t - e.born) / e.life;
            if (k < 0 || k >= 1) { fade(e.m, 0); return; }
            const age = (t - e.born) / 1000;
            const x = e.x + 30 * noise1(`${ctx.seed}:em${e.i}`, step * 0.5);
            place(e.m, x, FOOT - e.vy * age, { sx: e.size * (1 - k), sy: e.size * 1.6 * (1 - k), rot: e.spin * age });
            fade(e.m, 1);
          });

          // --- 灰
          ash.forEach((a) => {
            if (t < a.born) { fade(a.m, 0); return; }
            const free = (tt) => {
              const age = Math.max(0, tt - a.born) / 1000;
              return { x: a.x + a.sway * Math.sin(a.seed + age * 6), y: a.y - a.vy * age };
            };
            let x; let y; let s = 1;
            if (t < HIT) {
              const gs = Math.max(a.born, GATHER[0]);
              const base = free(Math.min(t, gs));
              const g = tween(t, gs, HIT, 'power2_in');
              const rx = base.x - CX; const ry = base.y - GY;
              const ang = g * 2.4;
              x = CX + (rx * Math.cos(ang) - ry * Math.sin(ang)) * (1 - g);
              y = GY + (rx * Math.sin(ang) + ry * Math.cos(ang)) * (1 - g);
              s = 1 - 0.4 * g;
            } else if (t < HIT + STOP) {
              x = CX + Math.cos(a.a) * 46 * a.seed / 100; y = GY + Math.sin(a.a) * 46 * a.seed / 100; s = 1;
            } else {
              const since = (t - HIT - STOP) / 1000;
              x = CX + Math.cos(a.a) * a.speed * since; y = GY + Math.sin(a.a) * a.speed * since + 600 * since * since; s = 0.6 + 0.6 * Math.min(1, since * 3);
            }
            const since = Math.max(0, (t - HIT - STOP) / 1000);
            const life = since / 0.42;
            place(a.m, x, y, { sx: a.size * s, sy: a.size * s * 0.8, rot: a.seed + a.spin * (t / 1000) });
            fade(a.m, life < 1 ? 1 - life * life : 0);
          });

          // --- 見出し: 叩きつける → 灰が集まる間に中央へ吸われて消える
          const suck = tween(t, GATHER[0], HIT, 'power2_in');
          formula.update(t, {
            y: FY + (GY - FY) * suck, toX: CX, suck, hideAt: HIT,
          });

          // --- 閃光（赤）
          fade(flash, t >= HIT && t < HIT + 34 ? 0.45 : 0);

          // --- 出るモンスター
          mon?.update(t, {
            at: MON, open: MON_OPEN, colorAt: MON_COLOR, nameAt: NAME_HIT, bandAt: BAND_HIT,
          });
        },
      };
    },
  }],
}, 'material_summon_tribute_effect.js');
